/**
 * SectionEmptyState — Drilldown Tab Header Standard §9.
 *
 * Empty is a routine state in a loan file, not an error. Write what to do, not
 * what's absent: "Link a document to get started." — not "No documents found."
 * No apologies, no exclamation marks, no icon above 24px.
 *
 * Inline empty VALUES inside a populated card are a different thing: those are
 * an em dash in `--muted` (see `orDash` in tabTokens) and get no treatment.
 */
import { Box } from "@mui/material";
import PropTypes from "prop-types";
import { cloneElement, isValidElement, memo } from "react";

import Flx from "../../../../components/layout/Flx";
import Txt from "../../../../components/typography/Txt";
import { TabGhostButton } from "./tabButtons";
import { TAB_TOKENS as T } from "./tabTokens";

const SectionEmptyState = ({ icon, message, action, sx }) => {
  const sizedIcon = isValidElement(icon)
    ? cloneElement(icon, {
        sx: { fontSize: 24, color: T.color.muted, ...(icon.props?.sx || {}) },
      })
    : icon;

  return (
    <Flx
      column
      center
      sx={{
        gap: "10px",
        paddingBlock: "40px",
        textAlign: "center",
        ...sx,
      }}
    >
      {sizedIcon}
      <Txt sx={{ ...T.type.pageSubtitle, maxWidth: 420 }}>{message}</Txt>
      {action && (
        <Box sx={{ mt: "2px" }}>
          {isValidElement(action) ? (
            action
          ) : (
            <TabGhostButton
              size="sm"
              label={action.label}
              icon={action.icon}
              onClick={action.onClick}
              disabled={action.disabled}
            />
          )}
        </Box>
      )}
    </Flx>
  );
};

SectionEmptyState.propTypes = {
  /** Optional icon. Never rendered larger than 24px. */
  icon: PropTypes.node,
  /** One sentence, in the imperative: what to do next. */
  message: PropTypes.node.isRequired,
  /** {label, onClick, icon} → ghost button, or pass your own element. */
  action: PropTypes.oneOfType([PropTypes.object, PropTypes.element]),
  sx: PropTypes.object,
};

export default memo(SectionEmptyState);
